import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Mail, Lock, User, Eye, EyeOff, LogIn } from 'lucide-react';
import { Button } from './ui/button';

// Use window.location.origin to always call the same domain (works in production and preview)
const getApiUrl = () => window.location.origin;

const AdminLogin = () => {
  const { isDark } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double submission
    if (loading) return;
    
    setLoading(true);
    setError('');

    const API_URL = getApiUrl();
    const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
    const requestBody = isLogin 
      ? { username: formData.username, password: formData.password }
      : { username: formData.username, email: formData.email, password: formData.password };

    // Create the body string ONCE before the fetch
    const bodyString = JSON.stringify(requestBody);

    try {
      // Use XMLHttpRequest for better iOS Safari compatibility
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}${endpoint}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.withCredentials = true;
        
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (parseError) {
              reject(new Error('Invalid response from server'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.detail || 'Authentication failed'));
            } catch {
              reject(new Error('Authentication failed'));
            }
          }
        };
        
        xhr.onerror = function() {
          reject(new Error('Network error. Please check your connection.'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('Request timed out. Please try again.'));
        };
        
        xhr.timeout = 30000; // 30 second timeout
        xhr.send(bodyString);
      });

      if (!result.access_token || !result.user) {
        throw new Error('Invalid response from server');
      }

      // Store credentials
      localStorage.setItem('adminToken', String(result.access_token));
      localStorage.setItem('adminUser', JSON.stringify({
        user_id: String(result.user.user_id || ''),
        username: String(result.user.username || ''),
        email: String(result.user.email || ''),
        name: String(result.user.name || result.user.username || ''),
        picture: result.user.picture ? String(result.user.picture) : '',
        role: String(result.user.role || 'admin')
      }));
      
      // Use replace to avoid back button issues
      window.location.replace('/admin/dashboard');
      
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ 
        backgroundColor: isDark ? '#000000' : '#f8fafc',
        background: isDark 
          ? 'radial-gradient(circle at 20% 50%, rgba(255, 0, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(0, 255, 255, 0.1) 0%, transparent 50%), #000000'
          : 'linear-gradient(135deg, #ffffff 0%, #fce7f3 50%, #ffffff 100%)'
      }}
    >
      <div 
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{
          backgroundColor: isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.98)',
          border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
          boxShadow: isDark 
            ? '0 20px 60px rgba(255, 0, 255, 0.3)' 
            : '0 20px 60px rgba(255, 20, 147, 0.2)'
        }}
      >
        <div className="text-center mb-8">
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ 
              color: isDark ? '#ffffff' : '#0f172a',
              textShadow: isDark ? '0 0 20px rgba(255, 255, 255, 0.5)' : 'none'
            }}
          >
            Admin Dashboard
          </h1>
          <p style={{ color: isDark ? '#a0a0a0' : '#64748b' }}>
            {isLogin ? 'Sign in to manage your website' : 'Create your admin account'}
          </p>
        </div>

        {error && (
          <div 
            className="mb-6 p-3 rounded-lg text-center"
            style={{ 
              backgroundColor: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#ef4444'
            }}
          >
            {error}
          </div>
        )}

        <Button
          type="button"
          onClick={handleGoogleLogin}
          className="w-full mb-6 py-3 flex items-center justify-center gap-3"
          style={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#ffffff',
            border: `2px solid ${isDark ? '#00ffff' : '#e2e8f0'}`,
            color: isDark ? '#ffffff' : '#0f172a'
          }}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span 
              className="px-4"
              style={{ 
                backgroundColor: isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.98)',
                color: isDark ? '#a0a0a0' : '#64748b'
              }}
            >
              or {isLogin ? 'sign in' : 'register'} with username
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: isDark ? '#a0a0a0' : '#374151' }}
            >
              Username
            </label>
            <div className="relative">
              <User 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
              />
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full pl-10 pr-4 py-3 rounded-lg transition-all duration-300"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                  border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
                placeholder="Enter username"
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
          </div>

          {!isLogin && (
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: isDark ? '#a0a0a0' : '#374151' }}
              >
                Email
              </label>
              <div className="relative">
                <Mail 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  style={{ color: isDark ? '#00ffff' : '#00bfff' }}
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 rounded-lg transition-all duration-300"
                  style={{
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                    border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                  placeholder="Enter email"
                  required={!isLogin}
                  autoComplete="email"
                />
              </div>
            </div>
          )}

          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: isDark ? '#a0a0a0' : '#374151' }}
            >
              Password
            </label>
            <div className="relative">
              <Lock 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: isDark ? '#ffff00' : '#f59e0b' }}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-10 pr-12 py-3 rounded-lg transition-all duration-300"
                style={{
                  backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : '#f8fafc',
                  border: `2px solid ${isDark ? '#333' : '#e2e8f0'}`,
                  color: isDark ? '#ffffff' : '#0f172a'
                }}
                placeholder="Enter password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                style={{ color: isDark ? '#666' : '#9ca3af' }}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full py-3 font-bold text-white transition-all duration-300 hover:scale-105"
            style={{
              background: isDark 
                ? 'linear-gradient(135deg, #ff00ff 0%, #ff0080 100%)'
                : 'linear-gradient(135deg, #ff1493 0%, #ff0080 100%)',
              boxShadow: isDark 
                ? '0 10px 40px rgba(255, 0, 255, 0.4)' 
                : '0 10px 40px rgba(255, 20, 147, 0.3)'
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5" />
                {isLogin ? 'Sign In' : 'Create Account'}
              </span>
            )}
          </Button>
        </form>

        <p 
          className="mt-6 text-center text-sm"
          style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
        >
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="font-semibold hover:underline"
            style={{ color: isDark ? '#ff00ff' : '#ff1493' }}
          >
            {isLogin ? 'Register' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
